import { ActivityItem } from 'app/data/types/activity';

/**
 * Statuses a user can still cancel out of.
 *
 * `checked_in` belongs here for the same reason it belongs in {@link CANCELLABLE_BOOKING_STATUS_KEYS}:
 * arriving does not end a registration, and someone who checks in and then has to leave is exactly who
 * needs to give the seat back. Whether the window is still open is `cancellableUntil`'s job.
 *
 * It is also the only spelling that reaches this code. An attended registration is STORED as
 * `attended`, but search-service renders it as `checked_in` while the event is still upcoming — "what
 * the user just did" — and keeps `attended` only in Completed. This set was written against the stored
 * event vocabulary (`registered | waitlisted | attended | cancelled`) and so never saw the token the
 * api actually sends. That read-time rewrite is also what keeps the entry safe: a finished event
 * arrives as `attended`, still absent from this set, so Cancel cannot appear on it.
 *
 * `cancelled` and `attended` stay out — nothing left to cancel.
 */
const CANCELLABLE_STATUS_KEYS = new Set(['registered', 'waitlisted', 'checked_in']);

/**
 * Whether self-cancellation is still open for this booking right now.
 *
 * `cancellableUntil` already encodes the whole policy — service-booking resolves it from the event's
 * cancellation-notice window, the booking's own effective price, and its session start — so there is
 * no arithmetic to repeat here. Absent means no deadline was published (a legacy document, or a
 * booking with no usable start), which fails closed rather than offering an action we cannot judge.
 */
export const isCancelOpen = (item: ActivityItem, now: number = Date.now()): boolean => {
  if (!CANCELLABLE_STATUS_KEYS.has(item.statusKey) || !item.cancellableUntil) {
    return false;
  }

  const closesAt = new Date(item.cancellableUntil).getTime();

  return !Number.isNaN(closesAt) && now < closesAt;
};

/**
 * Resource-booking statuses a user can still cancel out of.
 *
 * `pending_approval` because a request may be withdrawn, and `checked_in` because checking in does not
 * end a booking — someone who arrives, then has to leave, is exactly who needs to give the slot back.
 * Whether the window is still open is `cancellableUntil`'s job, not this set's.
 */
const CANCELLABLE_BOOKING_STATUS_KEYS = new Set(['confirmed', 'pending_approval', 'checked_in']);

const instant = (value?: string): number | undefined => {
  if (!value) {
    return undefined;
  }
  const time = new Date(value).getTime();

  return Number.isNaN(time) ? undefined : time;
};

/**
 * Whether self-cancellation is still open for a resource booking right now.
 *
 * Separate from {@link isCancelOpen} because the two verticals share neither a status vocabulary nor
 * a window shape: an event's is a single deadline, a booking's can have a hole in it.
 *
 * `cancellableUntil` is the resolved deadline and carries the common case on its own. The second
 * branch exists for the disjoint window: with `allowCancelDuring` and no `allowLateCancel`,
 * cancellation closes ahead of the start and REOPENS once the booking begins, which no single
 * timestamp can say. Missing the deadline fails closed — except in that reopened stretch, where the
 * policy is what grants it rather than the deadline that has already passed.
 */
export const isBookingCancelOpen = (item: ActivityItem, now: number = Date.now()): boolean => {
  if (item.type !== 'resource_booking' || !CANCELLABLE_BOOKING_STATUS_KEYS.has(item.statusKey)) {
    return false;
  }

  const closesAt = instant(item.cancellableUntil);
  if (closesAt !== undefined && now < closesAt) {
    return true;
  }

  if (!item.cancellationPolicy?.allowCancelDuring) {
    return false;
  }

  // `endTimestamp` is only set when the occurrence has a distinct end; without one there is no
  // "during" to reopen in. An unparseable bound fails closed rather than offering it indefinitely.
  const startsAt = instant(item.timestamp);
  const endsAt = instant(item.endTimestamp);

  return startsAt !== undefined && endsAt !== undefined && now >= startsAt && now <= endsAt;
};
